import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useQuery } from '@tanstack/react-query'
import { serviceCaseSchema, type ServiceCaseInput, serviceCaseStatuses, serviceCasePriorities, serviceCaseCategories, serviceCaseChannels } from '../../../shared/types'
import { apiRequest, assetsApi } from '../../lib/api'

interface ServiceCaseFormProps {
  defaultValues?: Partial<ServiceCaseInput>
  onSubmit: (data: ServiceCaseInput) => void
  onCancel: () => void
  loading?: boolean
  fixedAssetId?: string
}

export function ServiceCaseForm({ defaultValues, onSubmit, onCancel, loading, fixedAssetId }: ServiceCaseFormProps) {
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<ServiceCaseInput>({
    resolver: zodResolver(serviceCaseSchema),
    defaultValues: {
      status: 'open',
      priority: 'medium',
      category: 'other',
      assetId: fixedAssetId,
      ...defaultValues,
    },
  })

  const selectedAssetId = watch('assetId')

  // Fetch assets for dropdown (if not fixed)
  const { data: assetsResponse } = useQuery({
    queryKey: ['assets', 'all'],
    queryFn: () => assetsApi.list({ limit: 100 }),
    enabled: !fixedAssetId,
  })

  // Fetch users for assignment
  const { data: usersResponse } = useQuery({
    queryKey: ['users', 'all'],
    queryFn: () => apiRequest<{ data: { id: string; firstName: string | null; lastName: string | null; email: string }[] }>('/users'),
  })

  const assets = assetsResponse?.data || []
  const users = usersResponse?.data || []

  // When asset changes, update accountId
  const selectedAsset = assets.find(a => a.id === selectedAssetId)

  // Sync accountId whenever selectedAsset changes (including when assets load)
  useEffect(() => {
    if (selectedAsset) {
      setValue('accountId', selectedAsset.accountId)
    }
  }, [selectedAsset, setValue])

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      {/* Asset Selection */}
      {!fixedAssetId && (
        <div>
          <label className="block text-sm font-medium text-gray-700">
            Asset <span className="text-red-500">*</span>
          </label>
          <select
            {...register('assetId')}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
          >
            <option value="">Select an asset...</option>
            {assets.map((asset) => (
              <option key={asset.id} value={asset.id}>
                {asset.serialNumber} {asset.account ? `- ${asset.account.name}` : ''}
              </option>
            ))}
          </select>
          {errors.assetId && (
            <p className="mt-1 text-sm text-red-600">{errors.assetId.message}</p>
          )}
        </div>
      )}

      {/* Hidden accountId - auto-filled from asset via useEffect */}
      <input type="hidden" {...register('accountId')} />

      {/* Subject */}
      <div>
        <label className="block text-sm font-medium text-gray-700">
          Subject <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          {...register('subject')}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
          placeholder="Brief description of the issue..."
        />
        {errors.subject && (
          <p className="mt-1 text-sm text-red-600">{errors.subject.message}</p>
        )}
      </div>

      {/* Description */}
      <div>
        <label className="block text-sm font-medium text-gray-700">
          Description <span className="text-red-500">*</span>
        </label>
        <textarea
          {...register('description')}
          rows={4}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
          placeholder="Detailed description of the issue or request..."
        />
        {errors.description && (
          <p className="mt-1 text-sm text-red-600">{errors.description.message}</p>
        )}
      </div>

      {/* Priority & Category row */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700">Priority</label>
          <select
            {...register('priority')}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
          >
            {serviceCasePriorities.map((priority) => (
              <option key={priority} value={priority}>
                {priority.charAt(0).toUpperCase() + priority.slice(1)}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">Category</label>
          <select
            {...register('category')}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
          >
            {serviceCaseCategories.map((category) => (
              <option key={category} value={category}>
                {category.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Status (for editing) */}
      {defaultValues?.status && (
        <div>
          <label className="block text-sm font-medium text-gray-700">Status</label>
          <select
            {...register('status')}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
          >
            {serviceCaseStatuses.map((status) => (
              <option key={status} value={status}>
                {status.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Channel */}
      <div>
        <label className="block text-sm font-medium text-gray-700">Channel</label>
        <select
          {...register('channel')}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
        >
          <option value="">Select channel...</option>
          {serviceCaseChannels.map((channel) => (
            <option key={channel} value={channel}>
              {channel.charAt(0).toUpperCase() + channel.slice(1).replace('_', ' ')}
            </option>
          ))}
        </select>
      </div>

      {/* Assigned To */}
      <div>
        <label className="block text-sm font-medium text-gray-700">Assigned To</label>
        <select
          {...register('assignedToId')}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
        >
          <option value="">Unassigned</option>
          {users.map((user) => (
            <option key={user.id} value={user.id}>
              {user.firstName} {user.lastName} ({user.email})
            </option>
          ))}
        </select>
      </div>

      {/* Due Date */}
      <div>
        <label className="block text-sm font-medium text-gray-700">Due Date</label>
        <input
          type="datetime-local"
          {...register('dueDate')}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
        />
      </div>

      {/* Related Part Number (for part_request category) */}
      <div>
        <label className="block text-sm font-medium text-gray-700">Related Part Number</label>
        <input
          type="text"
          {...register('relatedPartNumber')}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
          placeholder="e.g., PART-001"
        />
      </div>

      {/* Actions */}
      <div className="flex justify-end gap-3 pt-4">
        <button
          type="button"
          onClick={onCancel}
          className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={loading}
          className="rounded-md bg-brand-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-brand-500 disabled:opacity-50"
        >
          {loading ? 'Saving...' : 'Save'}
        </button>
      </div>
    </form>
  )
}
